/**
 * Asserts that a code path is unreachable. Used for exhaustiveness checks
 * in switch statements — TypeScript narrows the value to `never` if all
 * cases are handled, and this function catches the rest at compile time.
 *
 * @example
 * ```ts
 * function getLabel(status: 'active' | 'inactive'): string {
 *   switch (status) {
 *     case 'active': return 'Active';
 *     case 'inactive': return 'Inactive';
 *     default: return assertUnreachable(status);
 *   }
 * }
 * ```
 *
 * If a new value is added to the union (e.g., `'pending'`), TypeScript
 * errors because `'pending'` is not assignable to `never`.
 */
export function assertUnreachable(value: never): never {
  throw new Error(`Unexpected value: ${JSON.stringify(value)}`);
}

export function isValidEnumValue<T extends string>(value: string, valid: readonly T[]): value is T {
  return (valid as readonly string[]).includes(value);
}
